// Globals
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Modules
import { Header } from '@/modules/Layout';
import { getSuggestedFaqStatusLabel, isSuggestedFaqActionable } from '@/modules/SuggestedFaq';
import {
  ApproveFaqModal,
  getSuggestedFaqFormValues,
  RejectFaqModal,
  StyledApproveButton,
  SuggestedFaqForm,
  TSuggestedFaqFormValues,
} from '@/modules/SuggestedFaqCommon';

// Models
import { TApproveSuggestedFaqTarget } from '@/models/suggestedFaq.model';

// Components
import { BackLink, useForm } from '@/components';
import { Button, Spinner } from '@/ui';
import { LockIcon } from '@/icons';
import { SuggestedFaqMetaInfo } from '../../components';
import {
  StyledActions,
  StyledCard,
  StyledContainer,
  StyledLoaderWrapper,
  StyledLockBanner,
  StyledTitlePill,
} from './SuggestedFaqDetailsContainerStyles';

// Helpers
import { ROUTES } from '@/constants/routes';
import { getShortId } from '@/utils';
import { useBoolean, useGoBack } from '@/hooks';
import { useChangeSuggestedFaqStatus, useGetSuggestedFaqById } from '../../hooks';

const SuggestedFaqDetailsContainer = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [formInstance] = useForm();
  const { suggestedFaq, isLoading } = useGetSuggestedFaqById(id);
  const { handleApproveSuggestedFaq, handleRejectSuggestedFaq } = useChangeSuggestedFaqStatus(id);
  const handleBackClick = useGoBack();

  const { value: isApproveModalOpen, setTrue: openApproveModal, setFalse: closeApproveModal } = useBoolean();
  const { value: isRejectModalOpen, setTrue: openRejectModal, setFalse: closeRejectModal } = useBoolean();

  const isActionable = isSuggestedFaqActionable(suggestedFaq?.status);
  const initialValues = useMemo(() => getSuggestedFaqFormValues(suggestedFaq), [suggestedFaq]);

  useEffect(() => {
    if (initialValues) {
      formInstance.setFieldsValue(initialValues);
    }
  }, [initialValues, formInstance]);

  const handleOpenApproveModal = async () => {
    try {
      await formInstance.validateFields();
      openApproveModal();
    } catch {}
  };

  const handleApprove = async (target: TApproveSuggestedFaqTarget) => {
    if (!suggestedFaq) return;

    try {
      const values = (await formInstance.validateFields()) as TSuggestedFaqFormValues;
      await handleApproveSuggestedFaq({ ...values, ...target, originalQuestion: suggestedFaq.question });
      closeApproveModal();
    } catch {}
  };

  const handleReject = async () => {
    await handleRejectSuggestedFaq();
    closeRejectModal();
  };

  if (!suggestedFaq) {
    return (
      <StyledLoaderWrapper>
        <Spinner />
      </StyledLoaderWrapper>
    );
  }

  return (
    <>
      <Header
        suptitle={
          <BackLink to={ROUTES.SUGGESTED_FAQS} onClick={handleBackClick}>
            {t('back_to_suggested_faq')}
          </BackLink>
        }
        title={
          <>
            {t('menu_suggested_faq_details', { id: getShortId(suggestedFaq.reviewId) })}
            <StyledTitlePill status={suggestedFaq.status}>
              {getSuggestedFaqStatusLabel(suggestedFaq.status, t)}
            </StyledTitlePill>
          </>
        }
      />
      <StyledContainer>
        <StyledCard>
          {!isActionable && (
            <StyledLockBanner>
              <LockIcon />
              <span>
                {t('label_faq_locked_prefix')} <strong>{getSuggestedFaqStatusLabel(suggestedFaq.status, t)}</strong>{' '}
                {t('label_faq_locked_suffix')}
              </span>
            </StyledLockBanner>
          )}
          <SuggestedFaqForm formInstance={formInstance} initialValues={initialValues} disabled={!isActionable} />
          <SuggestedFaqMetaInfo suggestedFaq={suggestedFaq} />
          {isActionable && (
            <StyledActions>
              <StyledApproveButton type="primary" onClick={handleOpenApproveModal} loading={isLoading}>
                {t('btn_approve')}
              </StyledApproveButton>
              <Button type="default" danger onClick={openRejectModal} loading={isLoading}>
                {t('btn_deny')}
              </Button>
            </StyledActions>
          )}
        </StyledCard>
      </StyledContainer>
      <ApproveFaqModal
        isOpen={isApproveModalOpen}
        onClose={closeApproveModal}
        onApprove={handleApprove}
        isLoading={isLoading}
        sourceChannels={suggestedFaq.sourceChannels}
      />
      <RejectFaqModal
        isOpen={isRejectModalOpen}
        onClose={closeRejectModal}
        onReject={handleReject}
        isLoading={isLoading}
      />
    </>
  );
};

export default SuggestedFaqDetailsContainer;
